import React, { useCallback } from 'react';
import { Space, Tooltip } from 'antd';

type HeaderIconButtonProps = {
  url: string;
  tooltipTitle: string;
  ariaLabel: string;
  icon: React.FC<{ className?: string }>;
};

/**
 * Reusable header icon button with tooltip; opens url in new tab.
 * Keyboard accessible (Enter/Space) and has aria-label for screen readers.
 */
export const HeaderIconButton: React.FC<HeaderIconButtonProps> = ({
  url,
  tooltipTitle,
  ariaLabel,
  icon: Icon,
}) => {
  const openUrl = useCallback(() => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [url]);

  return (
    <Tooltip
      title={tooltipTitle}
      color="#FFFFFF"
      styles={{
        body: {
          backgroundColor: '#FFFFFF',
          color: '#333333',
          padding: '8px 12px',
          borderRadius: '8px',
          border: '1px solid #E5E5EA',
          boxShadow: '0px 4px 8px rgba(0,0,0,0.05)',
        },
      }}
    >
      <Space
        role="button"
        tabIndex={0}
        onClick={openUrl}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openUrl();
          }
        }}
        aria-label={ariaLabel}
        className="!w-9 !h-9 flex items-center justify-center cursor-pointer bg-white/50 hover:bg-white/70 transition-background-color duration-200 w-fit !rounded-full"
      >
        <Icon />
      </Space>
    </Tooltip>
  );
};
